import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChevronDown, ChevronRight, FileSearch, CheckCircle2, XCircle, Clock, Plane } from 'lucide-react'
import type { PricingAudit, AuditedAdjustmentRule, AuditedMinimumHourRule } from '@limo/shared/pricing'

interface PricingAuditCardProps {
  pricingAudit: PricingAudit | null | undefined
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

const formatSkipReason = (reason: AuditedAdjustmentRule['skipReason']): string => {
  const labels: Record<string, string> = {
    inactive: 'Inactive',
    no_days_of_week: 'No days configured',
    day_of_week_mismatch: 'Day mismatch',
    date_before_start: 'Before start date',
    date_after_end: 'After end date',
    missing_time_range: 'Missing time range',
    equal_start_end_time: 'Invalid time range',
    invalid_time_values: 'Invalid time values',
    time_range_mismatch: 'Outside time range',
    vehicle_mismatch: 'Vehicle not targeted',
  }
  return reason ? labels[reason] || reason : ''
}

const formatMinHourSkipReason = (reason: AuditedMinimumHourRule['skipReason']): string => {
  const labels: Record<string, string> = {
    inactive: 'Inactive',
    no_days_of_week: 'No days configured',
    day_of_week_mismatch: 'Day mismatch',
    date_before_start: 'Before start date',
    date_after_end: 'After end date',
    all_day: 'All day (matched)',
    missing_time_range_partial: 'Incomplete time range',
    equal_start_end_time: 'Invalid time range',
    invalid_time_values: 'Invalid time values',
    time_range_mismatch: 'Outside time range',
    vehicle_mismatch: 'Vehicle not targeted',
  }
  return reason ? labels[reason] || reason : ''
}

export function PricingAuditCard({ pricingAudit }: PricingAuditCardProps) {
  const [isOpen, setIsOpen] = useState(true)
  const [settingsOpen, setSettingsOpen] = useState(false)

  if (!pricingAudit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileSearch className="h-5 w-5" />
            Pricing Audit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No audit data available. Audit data is recorded for bookings created after this feature was enabled.
          </p>
        </CardContent>
      </Card>
    )
  }

  const calculatedAt = new Date(pricingAudit.calculatedAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })

  const matchedCount = pricingAudit.consideredAdjustments.filter((r) => r.matched).length
  const skippedCount = pricingAudit.consideredAdjustments.filter((r) => !r.matched).length

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                <FileSearch className="h-5 w-5" />
                Pricing Audit
              </span>
              <span className="flex items-center gap-2">
                <span className="text-xs font-normal text-muted-foreground">
                  {calculatedAt}
                </span>
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </span>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            {/* Pricing Tier */}
            {pricingAudit.pricingTier && (
              <div>
                <h4 className="text-sm font-medium mb-2">Pricing Tier</h4>
                <div className="grid gap-2 text-sm sm:grid-cols-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <span className="capitalize">
                      {pricingAudit.pricingTier.pricingType.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base Price</span>
                    <span>{formatCurrency(pricingAudit.pricingTier.basePrice)}</span>
                  </div>
                  {pricingAudit.pricingTier.pricePerMile !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Per Mile</span>
                      <span>{formatCurrency(pricingAudit.pricingTier.pricePerMile)}</span>
                    </div>
                  )}
                  {pricingAudit.pricingTier.pricePerHour !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Per Hour</span>
                      <span>{formatCurrency(pricingAudit.pricingTier.pricePerHour)}</span>
                    </div>
                  )}
                  {pricingAudit.pricingTier.serviceFee !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Service Fee</span>
                      <span>{formatCurrency(pricingAudit.pricingTier.serviceFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tiered Pricing</span>
                    <span>{pricingAudit.pricingTier.useTieredPricing ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Snapshot */}
            <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium hover:underline">
                {settingsOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                Settings Snapshot
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                  {pricingAudit.settingsSnapshot.gratuityPercent !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Gratuity</span>
                      <span>{pricingAudit.settingsSnapshot.gratuityPercent}%</span>
                    </div>
                  )}
                  {pricingAudit.settingsSnapshot.tax && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax</span>
                        <span>
                          {pricingAudit.settingsSnapshot.tax.enabled
                            ? `${pricingAudit.settingsSnapshot.tax.rate}% (${pricingAudit.settingsSnapshot.tax.label})`
                            : 'Disabled'}
                        </span>
                      </div>
                    </>
                  )}
                  {pricingAudit.settingsSnapshot.fuelSurcharge && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fuel Surcharge</span>
                      <span>
                        {pricingAudit.settingsSnapshot.fuelSurcharge.enabled
                          ? `${pricingAudit.settingsSnapshot.fuelSurcharge.amount}${pricingAudit.settingsSnapshot.fuelSurcharge.type === 'percentage' ? '%' : ' flat'}`
                          : 'Disabled'}
                      </span>
                    </div>
                  )}
                  {pricingAudit.settingsSnapshot.minimumFare !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Minimum Fare</span>
                      <span>
                        {pricingAudit.settingsSnapshot.minimumFare > 0
                          ? formatCurrency(pricingAudit.settingsSnapshot.minimumFare)
                          : 'None'}
                      </span>
                    </div>
                  )}
                  {pricingAudit.settingsSnapshot.rounding && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rounding</span>
                      <span className="capitalize">
                        {pricingAudit.settingsSnapshot.rounding.direction} to{' '}
                        {pricingAudit.settingsSnapshot.rounding.roundTo.replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                  {pricingAudit.settingsSnapshot.airportFees && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Airport Fees</span>
                      <span>
                        {pricingAudit.settingsSnapshot.airportFees.enabled
                          ? `Pickup: ${formatCurrency(pricingAudit.settingsSnapshot.airportFees.pickupFee)} / Dropoff: ${formatCurrency(pricingAudit.settingsSnapshot.airportFees.dropoffFee)}`
                          : 'Disabled'}
                      </span>
                    </div>
                  )}
                  {pricingAudit.settingsSnapshot.meetGreet && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Meet & Greet</span>
                      <span>
                        {pricingAudit.settingsSnapshot.meetGreet.enabled
                          ? formatCurrency(pricingAudit.settingsSnapshot.meetGreet.fee)
                          : 'Disabled'}
                      </span>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Adjustment Rules Evaluated */}
            {pricingAudit.consideredAdjustments.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Adjustment Rules ({matchedCount} matched, {skippedCount} skipped)
                </h4>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Rule</TableHead>
                        <TableHead className="text-right">Priority</TableHead>
                        <TableHead className="text-right">Increase</TableHead>
                        <TableHead>Vehicle Scope</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pricingAudit.consideredAdjustments.map((rule) => {
                        const isApplied =
                          pricingAudit.appliedAdjustment?.id === rule.id
                        return (
                          <TableRow
                            key={rule.id}
                            className={
                              isApplied
                                ? 'bg-green-50 dark:bg-green-950/20'
                                : rule.matched
                                  ? 'bg-yellow-50 dark:bg-yellow-950/20'
                                  : ''
                            }
                          >
                            <TableCell className="w-8 pr-0">
                              {rule.matched ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-muted-foreground/40" />
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{rule.name}</TableCell>
                            <TableCell className="text-right">{rule.priority}</TableCell>
                            <TableCell className="text-right">
                              +{rule.percentageIncrease}%
                            </TableCell>
                            <TableCell>
                              {rule.vehicleIds
                                ? `${rule.vehicleIds.length} vehicle${rule.vehicleIds.length > 1 ? 's' : ''}`
                                : 'All'}
                            </TableCell>
                            <TableCell>
                              {isApplied ? (
                                <Badge variant="default" className="bg-green-600">
                                  Applied
                                </Badge>
                              ) : rule.matched ? (
                                <Badge variant="secondary">Matched</Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  {formatSkipReason(rule.skipReason)}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
                {pricingAudit.appliedAdjustment && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Applied: {pricingAudit.appliedAdjustment.name} (+
                    {pricingAudit.appliedAdjustment.percentageIncrease}% ={' '}
                    {formatCurrency(pricingAudit.appliedAdjustment.amount)})
                  </p>
                )}
              </div>
            )}

            {/* Minimum Hour Adjustment Evaluation */}
            {pricingAudit.minimumHourEvaluation && pricingAudit.minimumHourEvaluation.evaluated && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  Minimum Hour Adjustment
                  {pricingAudit.minimumHourEvaluation.airportExempt && (
                    <Badge variant="outline" className="ml-1 text-xs gap-1">
                      <Plane className="h-3 w-3" />
                      Airport Exempt
                    </Badge>
                  )}
                </h4>
                <div className="grid gap-2 text-sm sm:grid-cols-2 mb-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rules Considered</span>
                    <span>{pricingAudit.minimumHourEvaluation.rulesConsidered}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base Minimum Hours</span>
                    <span>{pricingAudit.minimumHourEvaluation.baseMinimumHours} hrs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rule Matched</span>
                    <span>
                      {pricingAudit.minimumHourEvaluation.matched ? (
                        <Badge variant="default" className="bg-green-600">Yes</Badge>
                      ) : (
                        <Badge variant="secondary">No</Badge>
                      )}
                    </span>
                  </div>
                  {pricingAudit.minimumHourEvaluation.matched && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Applied Minimum</span>
                      <span className="font-medium">
                        {pricingAudit.minimumHourEvaluation.airportExempt
                          ? `${pricingAudit.minimumHourEvaluation.baseMinimumHours} hrs (standard)`
                          : `${pricingAudit.minimumHourEvaluation.appliedMinimumHours} hrs`}
                      </span>
                    </div>
                  )}
                  {pricingAudit.minimumHourEvaluation.matchedRuleName && (
                    <div className="flex justify-between sm:col-span-2">
                      <span className="text-muted-foreground">Matched Rule</span>
                      <span className="font-medium">{pricingAudit.minimumHourEvaluation.matchedRuleName}</span>
                    </div>
                  )}
                </div>

                {/* Audited Rules Table */}
                {pricingAudit.minimumHourEvaluation.auditedRules.length > 0 && (
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Rule</TableHead>
                          <TableHead className="text-right">Priority</TableHead>
                          <TableHead className="text-right">Min Hours</TableHead>
                          <TableHead>Vehicle Scope</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pricingAudit.minimumHourEvaluation.auditedRules.map((rule) => {
                          const isApplied =
                            pricingAudit.minimumHourEvaluation?.matchedRuleId === rule.id
                          return (
                            <TableRow
                              key={rule.id}
                              className={
                                isApplied
                                  ? pricingAudit.minimumHourEvaluation?.airportExempt
                                    ? 'bg-amber-50 dark:bg-amber-950/20'
                                    : 'bg-green-50 dark:bg-green-950/20'
                                  : rule.matched
                                    ? 'bg-yellow-50 dark:bg-yellow-950/20'
                                    : ''
                              }
                            >
                              <TableCell className="w-8 pr-0">
                                {rule.matched ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-muted-foreground/40" />
                                )}
                              </TableCell>
                              <TableCell className="font-medium">{rule.name}</TableCell>
                              <TableCell className="text-right">{rule.priority}</TableCell>
                              <TableCell className="text-right">{rule.minimumHours} hrs</TableCell>
                              <TableCell>
                                {rule.vehicleIds
                                  ? `${rule.vehicleIds.length} vehicle${rule.vehicleIds.length > 1 ? 's' : ''}`
                                  : 'All'}
                              </TableCell>
                              <TableCell>
                                {isApplied ? (
                                  pricingAudit.minimumHourEvaluation?.airportExempt ? (
                                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                                      Exempt
                                    </Badge>
                                  ) : (
                                    <Badge variant="default" className="bg-green-600">
                                      Applied
                                    </Badge>
                                  )
                                ) : rule.matched ? (
                                  <Badge variant="secondary">Matched</Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    {formatMinHourSkipReason(rule.skipReason)}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {/* Flat Rate Evaluation */}
            {pricingAudit.flatRateEvaluation && (
              <div>
                <h4 className="text-sm font-medium mb-2">Flat Rate Evaluation</h4>
                <div className="grid gap-2 text-sm sm:grid-cols-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Overrides Checked</span>
                    <span>{pricingAudit.flatRateEvaluation.overridesChecked}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Match</span>
                    <span>
                      {pricingAudit.flatRateEvaluation.matched ? (
                        <Badge variant="default" className="bg-green-600">
                          Matched
                        </Badge>
                      ) : (
                        <Badge variant="secondary">No Match</Badge>
                      )}
                    </span>
                  </div>
                  {pricingAudit.flatRateEvaluation.matched && (
                    <>
                      {pricingAudit.flatRateEvaluation.matchedOverrideName && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Override</span>
                          <span>{pricingAudit.flatRateEvaluation.matchedOverrideName}</span>
                        </div>
                      )}
                      {pricingAudit.flatRateEvaluation.matchedPickupZone && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Zones</span>
                          <span>
                            {pricingAudit.flatRateEvaluation.matchedPickupZone} →{' '}
                            {pricingAudit.flatRateEvaluation.matchedDropoffZone}
                          </span>
                        </div>
                      )}
                      {pricingAudit.flatRateEvaluation.flatRate !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Flat Rate</span>
                          <span>{formatCurrency(pricingAudit.flatRateEvaluation.flatRate)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Deadhead Evaluation */}
            {pricingAudit.deadheadEvaluation && pricingAudit.deadheadEvaluation.enabled && (
              <div>
                <h4 className="text-sm font-medium mb-2">Deadhead Fees</h4>
                <div className="grid gap-2 text-sm">
                  {pricingAudit.deadheadEvaluation.hubAddress && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Hub</span>
                      <span className="text-right max-w-[200px] truncate">
                        {pricingAudit.deadheadEvaluation.hubAddress}
                      </span>
                    </div>
                  )}
                  {pricingAudit.deadheadEvaluation.pickup && (
                    <div className="rounded bg-muted/50 p-2 space-y-1">
                      <p className="text-xs font-medium">Pickup Deadhead</p>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Distance from hub</span>
                        <span>
                          {pricingAudit.deadheadEvaluation.pickup.distanceFromHub !== null
                            ? `${pricingAudit.deadheadEvaluation.pickup.distanceFromHub.toFixed(1)} mi`
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Miles included</span>
                        <span>{pricingAudit.deadheadEvaluation.pickup.milesIncluded} mi</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Chargeable miles</span>
                        <span>
                          {pricingAudit.deadheadEvaluation.pickup.chargeableMiles.toFixed(1)} mi
                        </span>
                      </div>
                      <div className="flex justify-between text-xs font-medium">
                        <span>Fee</span>
                        <span>{formatCurrency(pricingAudit.deadheadEvaluation.pickup.fee)}</span>
                      </div>
                    </div>
                  )}
                  {pricingAudit.deadheadEvaluation.dropoff && (
                    <div className="rounded bg-muted/50 p-2 space-y-1">
                      <p className="text-xs font-medium">Dropoff Deadhead</p>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Distance from hub</span>
                        <span>
                          {pricingAudit.deadheadEvaluation.dropoff.distanceFromHub !== null
                            ? `${pricingAudit.deadheadEvaluation.dropoff.distanceFromHub.toFixed(1)} mi`
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Miles included</span>
                        <span>{pricingAudit.deadheadEvaluation.dropoff.milesIncluded} mi</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Chargeable miles</span>
                        <span>
                          {pricingAudit.deadheadEvaluation.dropoff.chargeableMiles.toFixed(1)} mi
                        </span>
                      </div>
                      <div className="flex justify-between text-xs font-medium">
                        <span>Fee</span>
                        <span>
                          {formatCurrency(pricingAudit.deadheadEvaluation.dropoff.fee)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Minimum Fare */}
            {pricingAudit.minimumFareApplied && (
              <div>
                <h4 className="text-sm font-medium mb-1">Minimum Fare</h4>
                <p className="text-sm text-muted-foreground">
                  Minimum fare of {formatCurrency(pricingAudit.minimumFareThreshold ?? 0)} was
                  applied because the calculated subtotal was below the threshold.
                </p>
              </div>
            )}

            {/* Rounding */}
            {pricingAudit.roundingApplied && (
              <div>
                <h4 className="text-sm font-medium mb-2">Rounding</h4>
                <div className="grid gap-2 text-sm sm:grid-cols-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pre-rounding total</span>
                    <span>{formatCurrency(pricingAudit.roundingApplied.preRoundingTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Post-rounding total</span>
                    <span>{formatCurrency(pricingAudit.roundingApplied.postRoundingTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Method</span>
                    <span className="capitalize">
                      {pricingAudit.roundingApplied.direction} to{' '}
                      {pricingAudit.roundingApplied.roundTo.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
